import { useMutation } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';
import { type EntityType } from '~/types/entity';

import { vestigasApi } from '~/services/kyClient';

type AddToOrganizationalUnitParams = {
  entityId: UUID;
  entityType: EntityType;
  organizationalUnitId: UUID;
};

/**
 * Adds an entity to an organizational unit.
 * @see https://app.dev.vestigas.com/redoc#tag/Access-Control/operation/add_entity_to_org_unit_access_control_org_unit__org_unit_id___entity_type__post
 * @param organizationalUnitId - UUID of the organizational unit to add the entity to
 * @param entityId - UUID of the entity being added
 * @param entityType - Type of entity being added
 * @returns Promise containing the response with the created relationship ID
 * @throws Will throw an error if the API call fails
 */
export const addToOrganizationalUnit = async (
  organizationalUnitId: UUID,
  entityType: EntityType,
  entityId: UUID,
) => {
  const response = await vestigasApi
    .post(
      ENDPOINT.ORGANIZATIONAL_UNIT.ADD_TO(organizationalUnitId, entityType),
      {
        json: { entityId },
      },
    )
    .json<{ id: UUID }>();

  return response;
};

/**
 * React Query mutation hook for adding an entity to an organizational unit.
 * @param options - Optional React Query mutation options
 * @returns Mutation object containing mutation function and state
 */
export const useMutationAddToOrganizationalUnit = (
  options?: Parameters<typeof useMutation>[0],
) =>
  useMutation({
    mutationFn: async ({
      entityId,
      entityType,
      organizationalUnitId,
    }: AddToOrganizationalUnitParams) =>
      addToOrganizationalUnit(organizationalUnitId, entityType, entityId),
    ...options,
  });
